import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';
import type { ComponentPropsWithRef, CSSProperties, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

type TableAlign = 'left' | 'center' | 'right';

export type TableSortDirection = 'asc' | 'desc';

const alignClassName: Record<TableAlign, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

function TableContainer({ className, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return <div className={cn('relative w-full overflow-x-auto bg-container', className)} {...props} />;
}

function Table({ className, ...props }: ComponentPropsWithRef<'table'>): ReactElement {
    return <table className={cn('w-full border-separate border-spacing-0', className)} {...props} />;
}

function TableHeader({ className, ...props }: ComponentPropsWithRef<'thead'>): ReactElement {
    return <thead data-slot="table-header" className={cn('sticky top-0 z-20', className)} {...props} />;
}

function TableBody(props: ComponentPropsWithRef<'tbody'>): ReactElement {
    return <tbody {...props} />;
}

function TableRow({ className, onClick, ...props }: ComponentPropsWithRef<'tr'>): ReactElement {
    return (
        <tr
            className={cn(
                'group/row bg-container transition-colors hover:bg-neutral-25 data-[state=selected]:bg-blue-50',
                onClick && 'cursor-pointer',
                className,
            )}
            onClick={onClick}
            {...props}
        />
    );
}

const ariaSort: Record<TableSortDirection | 'none', 'ascending' | 'descending' | 'none'> = {
    asc: 'ascending',
    desc: 'descending',
    none: 'none',
};

export type TableColumnLayout = {
    pinned?: 'start' | 'end';
    pinOffset?: number;
    size?: number;
};

function columnLayoutStyle(
    { pinned, pinOffset, size }: TableColumnLayout,
    style: CSSProperties | undefined,
): CSSProperties {
    return {
        left: pinned === 'start' ? pinOffset : undefined,
        right: pinned === 'end' ? pinOffset : undefined,
        width: size,
        minWidth: size,
        ...style,
    };
}

export type TableHeadProps = ComponentPropsWithRef<'th'> &
    TableColumnLayout & {
        align?: TableAlign;
        sortDirection?: TableSortDirection | false;
    };

function TableHead({
    className,
    align = 'left',
    sortDirection,
    pinned,
    pinOffset,
    size,
    style,
    ...props
}: TableHeadProps): ReactElement {
    return (
        <th
            aria-sort={sortDirection === undefined ? undefined : ariaSort[sortDirection || 'none']}
            className={cn(
                'bg-container px-cozy pt-default pb-default align-bottom text-body whitespace-nowrap text-content-weaker first:pl-relaxed last:pr-8',
                pinned ? 'sticky z-1' : 'relative',
                alignClassName[align],
                className,
            )}
            style={columnLayoutStyle({ pinned, pinOffset, size }, style)}
            {...props}
        />
    );
}

function TableHeadContent({ className, ...props }: ComponentPropsWithRef<'span'>): ReactElement {
    return <span className={cn('inline-flex items-center gap-tight align-bottom', className)} {...props} />;
}

function TableSortButton({ className, ...props }: ComponentPropsWithRef<'button'>): ReactElement {
    return (
        <button
            className={cn(
                'cursor-pointer whitespace-nowrap outline-hidden focus-visible:underline',
                className,
            )}
            type="button"
            {...props}
        />
    );
}

export type TableSortIconProps = ComponentPropsWithRef<'button'> & {
    direction: TableSortDirection | undefined;
};

function TableSortIcon({ className, direction, ...props }: TableSortIconProps): ReactElement {
    const Icon =
        direction === 'asc' ? ChevronUpIcon : direction === 'desc' ? ChevronDownIcon : ChevronsUpDownIcon;

    return (
        <button
            aria-hidden
            className={cn('flex cursor-pointer', className)}
            tabIndex={-1}
            type="button"
            {...props}
        >
            <Icon
                className={cn(
                    'size-3.5 shrink-0',
                    direction ? 'stroke-[2.5] text-brand' : 'text-neutral-300',
                )}
            />
        </button>
    );
}

export type TableCellProps = ComponentPropsWithRef<'td'> &
    TableColumnLayout & {
        align?: TableAlign;
        dimmed?: boolean;
    };

function TableCell({
    className,
    align = 'left',
    dimmed = false,
    pinned,
    pinOffset,
    size,
    style,
    ...props
}: TableCellProps): ReactElement {
    return (
        <td
            className={cn(
                'border-b border-line-weakest bg-inherit px-cozy py-2.5 align-middle text-body text-content first:pl-relaxed last:pr-8 group-last/row:border-b-0',
                pinned ? 'sticky z-1' : 'relative',
                dimmed && 'opacity-50',
                alignClassName[align],
                className,
            )}
            style={columnLayoutStyle({ pinned, pinOffset, size }, style)}
            {...props}
        />
    );
}

function List({ className, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return (
        <div
            className={cn('relative w-full overflow-y-auto bg-container text-body text-content', className)}
            {...props}
        />
    );
}

function ListHeader({ className, ...props }: ComponentPropsWithRef<'button'>): ReactElement {
    return (
        <button
            data-slot="list-header"
            className={cn(
                'sticky top-0 z-20 flex w-full items-end justify-between gap-default bg-container px-relaxed pt-default pb-cozy text-caption text-content-weaker enabled:cursor-pointer',
                className,
            )}
            type="button"
            {...props}
        />
    );
}

export type ListHeaderLabelProps = ComponentPropsWithRef<'span'> & {
    direction: TableSortDirection | undefined;
};

function ListHeaderLabel({ children, className, direction, ...props }: ListHeaderLabelProps): ReactElement {
    const Icon = direction === 'asc' ? ChevronUpIcon : ChevronDownIcon;

    return (
        <span
            className={cn(
                'flex items-center gap-0.5 whitespace-nowrap',
                direction !== undefined && 'text-brand',
                className,
            )}
            {...props}
        >
            {direction !== undefined && <Icon aria-hidden className="size-3 shrink-0 stroke-[2.5]" />}
            {children}
        </span>
    );
}

function ListRow({ className, onClick, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return (
        <div
            className={cn(
                'flex min-h-14 items-center gap-default border-b border-line-weakest px-relaxed py-2.5 transition-colors last:border-b-0 data-[state=selected]:bg-blue-50',
                onClick && 'cursor-pointer hover:bg-neutral-25',
                className,
            )}
            onClick={onClick}
            {...props}
        />
    );
}

function ListRowStart({ className, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return <div className={cn('min-w-0 flex-1', className)} {...props} />;
}

function ListRowEnd({ className, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return <div className={cn('flex shrink-0 flex-col items-end text-right', className)} {...props} />;
}

function ListPlaceholder({ className, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    return <div className={cn('flex h-96 items-center justify-center', className)} {...props} />;
}

export {
    List,
    ListHeader,
    ListHeaderLabel,
    ListPlaceholder,
    ListRow,
    ListRowEnd,
    ListRowStart,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeadContent,
    TableHeader,
    TableRow,
    TableSortButton,
    TableSortIcon,
};
